import {
  Alert,
  AlertIcon,
  Box,
  Flex,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Input,
  InputGroup,
  InputRightAddon,
  Spinner,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { NumericInput } from "../../../../../common/input/NumericInput";
import { RadioOptionGroup } from "../../../../../common/input/RadioOptionGroup";
import { StepNavigation } from "../../../../../common/StepNavigation";
import {
  ContractNature,
  Currency,
  type SourcingEventV2Dto,
  type SourcingEventV2PatchDto,
} from "../../../../../autogen/bff-api";
import { CONTRACT_NATURE_OPTIONS } from "../utils/publicProcurementOptions";
import { foaPartBanner } from "../utils/foaPartDisplay";
import { useEditor, useFieldErrorTranslation } from "../../../../../common/editing/save-coordinator";

interface Props {
  eventId: string;
  nextStep?: () => void;
  nextStepLabel?: string;
}

export const InfoStep = ({ eventId, nextStep, nextStepLabel }: Props) => {
  const { t } = useTranslation();
  const { state, updateField, fieldStatus, fieldError } = useEditor<SourcingEventV2Dto, SourcingEventV2PatchDto>(eventId);
  const formatFieldError = useFieldErrorTranslation();

  const title = state.eventData.title;
  const estimatedValue = state.publicContext.estimatedValue?.value ?? null;
  const contractNature = state.publicContext.contractNature ?? "";
  const foaPart = state.publicContext.foaPart ?? null;
  const banner = foaPartBanner(foaPart, estimatedValue);

  const isSaving = (field: string) => fieldStatus(field) === "saving" || fieldStatus(field) === "pending";
  const titleFieldError = fieldError("eventData.title");
  const estimatedValueFieldError = fieldError("publicContext.estimatedValue.value");

  return (
    <Box pb="8" pt="10">
      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("General information")}
        </FormLabel>

        <FormControl isRequired mb="2" isInvalid={!!titleFieldError}>
          <Flex align="center" gap="1">
            <FormLabel mb="0">{t("Title")}</FormLabel>
            {isSaving("eventData.title") && <Spinner size="xs" />}
          </Flex>
          <Input
            type="text"
            value={title}
            placeholder={t("E.g. Office supplies for municipality of Oslo") ?? undefined}
            onChange={(e) => updateField({ eventData: { title: e.target.value } }, { debounceMs: 300 })}
          />
          {titleFieldError ? (
            <FormErrorMessage>{formatFieldError(titleFieldError)}</FormErrorMessage>
          ) : (
            <FormHelperText>{t("A short, descriptive title for the procurement")}</FormHelperText>
          )}
        </FormControl>
      </Box>

      <Box borderWidth="1px" borderRadius="lg" p="5" mb="8" bg="smBackground">
        <FormLabel fontWeight="bold" fontSize="md" mb="4">
          {t("Classification and value")}
        </FormLabel>

        <FormControl mb="6" isInvalid={!!estimatedValueFieldError}>
          <FormLabel>
            {t("Estimated value")} {isSaving("publicContext.estimatedValue") && <Spinner size="xs" />}
          </FormLabel>
          <InputGroup>
            <NumericInput
              numericValue={estimatedValue ? Number(estimatedValue) : null}
              placeholder="0"
              onNumericChange={(value) =>
                updateField(
                  { publicContext: { estimatedValue: { value, currency: Currency.Nok } } },
                  { debounceMs: 300 },
                )
              }
            />
            <InputRightAddon>{t("NOK")}</InputRightAddon>
          </InputGroup>
          {estimatedValueFieldError && (
            <FormErrorMessage>{formatFieldError(estimatedValueFieldError)}</FormErrorMessage>
          )}
        </FormControl>

        <FormControl>
          <FormLabel>
            {t("Contract type")} {isSaving("publicContext.contractNature") && <Spinner size="xs" />}
          </FormLabel>
          <RadioOptionGroup
            options={CONTRACT_NATURE_OPTIONS}
            value={contractNature}
            allowDeselect
            onChange={(value) =>
              updateField({
                publicContext: { contractNature: value ? (value as ContractNature) : null },
              })
            }
          />
        </FormControl>

        {banner && !estimatedValueFieldError && (
          <Alert status={banner.status} mt="4" borderRadius="md" fontSize="sm">
            <AlertIcon />
            {t(banner.messageKey)}
          </Alert>
        )}
      </Box>

      <StepNavigation onNext={nextStep} nextLabel={nextStepLabel} />
    </Box>
  );
};
